import React from 'react';
import { Grid, Box, Typography, Container } from '@mui/material';
import { useRole } from '../../contexts/RoleContext';
import { convertUserRoleToContextRole } from '../../utils/role-helpers';
import { EventRole, SystemRole, UserContextRole } from '../../types/enums/user-roles';
import { Widget } from './Widget';
import { WidgetType } from '../../types/dashboard';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { DASHBOARD_ENDPOINTS } from '../../constants/dashboard-endpoints';
import type { ParseKeys } from 'i18next';

/**
 * AD-451. Ключи конфигураций ниже проверяет КОМПИЛЯТОР, а не только проба.
 *
 * Прежде переводчик передавался сюда как `(key: string) => string`, и строгая
 * типизация ключей i18next (`src/types/i18next.d.ts`) на этой границе терялась:
 * `t('dashboard.organizer.title')` компилировался молча, хотя внутри
 * пространства `dashboard` такого ключа нет. `ParseKeys<'dashboard'>` — это
 * объединение ВСЕХ путей словаря `dashboard`, поэтому лишний префикс теперь
 * ошибка сборки, а не строка на экране пользователя.
 */
type DashboardT = (key: ParseKeys<'dashboard'>) => string;

// Упрощенная конфигурация виджета
export interface SimpleWidgetConfig {
  id: string;
  type: WidgetType;
  title: string;
  dataSource?: string;
  refreshInterval?: number;
}

// Интерфейс для конфигурации дашборда роли
export interface RoleDashboardConfig {
  title: string;
  description: string;
  widgets: SimpleWidgetConfig[];
  gridLayout: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  }[];
}

// Конфигурации дашбордов для ролей
const getOrganizerDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('organizer.title'),
  description: t('organizer.description'),
  widgets: [
    {
      id: 'organizer-metrics',
      type: 'kpi',
      title: t('organizer.widgets.metrics'),
      dataSource: DASHBOARD_ENDPOINTS.ORGANIZER.METRICS,
      refreshInterval: 30000,
    },
    {
      id: 'recent-events',
      type: 'list',
      title: t('organizer.widgets.recentEvents'),
      dataSource: DASHBOARD_ENDPOINTS.ORGANIZER.EVENTS_RECENT,
      refreshInterval: 60000,
    },
    {
      id: 'pending-tasks',
      type: 'list',
      title: t('organizer.widgets.pendingTasks'),
      dataSource: DASHBOARD_ENDPOINTS.ORGANIZER.TASKS_PENDING,
      refreshInterval: 30000,
    },
    {
      id: 'budget-overview',
      type: 'kpi',
      title: t('organizer.widgets.budgetOverview'),
      dataSource: DASHBOARD_ENDPOINTS.ORGANIZER.BUDGET_OVERVIEW,
      refreshInterval: 300000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 8, lg: 8 },
    { xs: 12, sm: 12, md: 4, lg: 4 },
    { xs: 12, sm: 6, md: 6, lg: 6 },
    { xs: 12, sm: 6, md: 6, lg: 6 },
  ],
});

const getSupplierDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('supplier.title'),
  description: t('supplier.description'),
  widgets: [
    {
      id: 'supplier-metrics',
      type: 'kpi',
      title: t('supplier.widgets.metrics'),
      dataSource: DASHBOARD_ENDPOINTS.SUPPLIER.METRICS,
      refreshInterval: 30000,
    },
    {
      id: 'active-orders',
      type: 'list',
      title: t('supplier.widgets.activeOrders'),
      dataSource: DASHBOARD_ENDPOINTS.SUPPLIER.ORDERS_ACTIVE,
      refreshInterval: 30000,
    },
    {
      id: 'revenue-chart',
      type: 'chart',
      title: t('supplier.widgets.revenueChart'),
      dataSource: DASHBOARD_ENDPOINTS.SUPPLIER.ANALYTICS_REVENUE,
      refreshInterval: 300000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 12, lg: 12 },
  ],
});

const getParticipantDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('participant.title'),
  description: t('participant.description'),
  widgets: [
    {
      id: 'upcoming-events',
      type: 'list',
      title: t('participant.widgets.upcomingEvents'),
      dataSource: DASHBOARD_ENDPOINTS.PARTICIPANT.EVENTS_UPCOMING,
      refreshInterval: 300000,
    },
    {
      // AD-419 / B4 (22.09.2026): виджет читает тот же маршрут, что вкладка
      // «Ближайшие события», и подбора за ним нет. Заголовок больше не обещает
      // персонализацию; id оставлен прежним — по нему сохраняется раскладка.
      id: 'recommendations',
      type: 'list',
      title: t('participant.widgets.nearestEvents'),
      dataSource: DASHBOARD_ENDPOINTS.PARTICIPANT.RECOMMENDATIONS,
      refreshInterval: 600000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
  ],
});

const getSpeakerDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('speaker.title'),
  description: t('speaker.description'),
  widgets: [
    {
      id: 'speaker-presentations',
      type: 'list',
      title: t('speaker.widgets.presentations'),
      dataSource: DASHBOARD_ENDPOINTS.SPEAKER.PRESENTATIONS,
      refreshInterval: 300000,
    },
    {
      id: 'speaker-schedule',
      type: 'list',
      title: t('speaker.widgets.schedule'),
      dataSource: DASHBOARD_ENDPOINTS.SPEAKER.SCHEDULE,
      refreshInterval: 600000,
    },
    {
      id: 'speaker-materials',
      type: 'list',
      title: t('speaker.widgets.materials'),
      dataSource: DASHBOARD_ENDPOINTS.SPEAKER.MATERIALS,
      refreshInterval: 300000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 12, lg: 12 },
  ],
});

const getAdminDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('admin.title'),
  description: t('admin.description'),
  widgets: [
    {
      id: 'admin-system-metrics',
      type: 'kpi',
      title: t('admin.widgets.systemMetrics'),
      dataSource: DASHBOARD_ENDPOINTS.ADMIN.SYSTEM_METRICS,
      refreshInterval: 30000,
    },
    {
      id: 'admin-user-management',
      type: 'list',
      title: t('admin.widgets.userManagement'),
      dataSource: DASHBOARD_ENDPOINTS.ADMIN.USERS,
      refreshInterval: 300000,
    },
    {
      id: 'admin-system-logs',
      type: 'list',
      title: t('admin.widgets.systemLogs'),
      dataSource: DASHBOARD_ENDPOINTS.ADMIN.LOGS,
      refreshInterval: 60000,
    },
    {
      id: 'admin-analytics',
      type: 'chart',
      title: t('admin.widgets.analytics'),
      dataSource: DASHBOARD_ENDPOINTS.ADMIN.ANALYTICS,
      refreshInterval: 300000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
    { xs: 12, sm: 12, md: 6, lg: 6 },
  ],
});

const getManagerDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('manager.title'),
  description: t('manager.description'),
  widgets: [
    {
      id: 'manager-platform-metrics',
      type: 'kpi',
      title: t('manager.widgets.platformMetrics'),
      dataSource: DASHBOARD_ENDPOINTS.MANAGER.PLATFORM_METRICS,
      refreshInterval: 30000,
    },
    {
      id: 'manager-events-overview',
      type: 'list',
      title: t('manager.widgets.eventsOverview'),
      dataSource: DASHBOARD_ENDPOINTS.MANAGER.EVENTS,
      refreshInterval: 300000,
    },
    {
      id: 'manager-user-analytics',
      type: 'chart',
      title: t('manager.widgets.userAnalytics'),
      dataSource: DASHBOARD_ENDPOINTS.MANAGER.ANALYTICS_USERS,
      refreshInterval: 300000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 8, lg: 8 },
    { xs: 12, sm: 12, md: 4, lg: 4 },
    { xs: 12, sm: 12, md: 12, lg: 12 },
  ],
});

const getDefaultDashboardConfig = (t: DashboardT): RoleDashboardConfig => ({
  title: t('default.title'),
  description: t('default.description'),
  widgets: [
    {
      id: 'default-welcome',
      type: 'kpi',
      title: t('default.widgets.welcome'),
      dataSource: DASHBOARD_ENDPOINTS.DEFAULT.WELCOME,
      refreshInterval: 86400000,
    },
  ],
  gridLayout: [
    { xs: 12, sm: 12, md: 12, lg: 12 },
  ],
});

// Основная функция выбора конфигурации
const getDashboardConfig = (role: UserContextRole, t: DashboardT): RoleDashboardConfig => {
  switch (role) {
    case EventRole.ORGANIZER:
      return getOrganizerDashboardConfig(t);
    case EventRole.SUPPLIER:
      return getSupplierDashboardConfig(t);
    case EventRole.PARTICIPANT:
      return getParticipantDashboardConfig(t);
    case EventRole.SPEAKER:
      return getSpeakerDashboardConfig(t);
    case SystemRole.ADMIN:
      return getAdminDashboardConfig(t);
    case SystemRole.MANAGER:
      return getManagerDashboardConfig(t);
    default:
      return getDefaultDashboardConfig(t);
  }
};

// Функция преобразования в полную конфигурацию виджета
const createFullWidgetConfig = (simpleConfig: SimpleWidgetConfig) => {
  return {
    id: simpleConfig.id,
    type: simpleConfig.type,
    title: simpleConfig.title,
    description: '',
    size: {
      w: 6,
      h: 4,
      minW: 2,
      minH: 2,
    },
    position: {
      x: 0,
      y: 0,
    },
    dataSource: simpleConfig.dataSource ?? '',
    ...(simpleConfig.refreshInterval != null ? { refreshInterval: simpleConfig.refreshInterval } : {}),
    // Данные будут загружаться через API в компоненте Widget
    data: null,
  };
};

/**
 * Основной компонент RoleDashboardLayout
 */
export const RoleDashboardLayout: React.FC = () => {
  const { currentRole } = useRole();
  /*
   * AD-451 (22.09.2026). Переводчик привязан к пространству `dashboard`, и
   * ключи ниже задаются ОТНОСИТЕЛЬНО него. До этой правки все они начинались с
   * `dashboard.`, то есть искались как `dashboard.dashboard.organizer.title` —
   * такого ключа в словаре нет, а i18next на ненайденном ключе возвращает сам
   * ключ. Панель ролей печатала на экран «dashboard.organizer.title» вместо
   * заголовка, на всех пяти языках и во всех ролях.
   *
   * Гарантия держится пробой `__tests__/RoleDashboardLayout.i18n.ad-451.test.tsx`:
   * панель каждой достижимой роли выводится на настоящем словаре, и в разметке
   * не должно быть ни подстроки `dashboard.`, ни любого сырого ключа.
   */
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const contextRole = convertUserRoleToContextRole(currentRole);
  const tStr = (key: string) => {
    const r = t(key);
    return typeof r === 'string' ? r : String(r);
  };
  const dashboardConfig = getDashboardConfig(contextRole || SystemRole.USER, tStr);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header секция */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            mb: 1,
          }}
        >
          {dashboardConfig.title}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {dashboardConfig.description}
        </Typography>
      </Box>

      {/* Виджеты в Grid Layout */}
      <Grid container spacing={3}>
        {dashboardConfig.widgets.map((widget, index) => {
          const gridProps = dashboardConfig.gridLayout[index] || { xs: 12, sm: 12, md: 6, lg: 6 };
          const fullWidgetConfig = createFullWidgetConfig(widget);
          
          return (
            <Grid 
              item 
              key={widget.id}
              xs={gridProps.xs}
              sm={gridProps.sm}
              md={gridProps.md}
              lg={gridProps.lg}
            >
              <Widget config={fullWidgetConfig} />
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default RoleDashboardLayout;